// 起動：素材の読み込み → ログイン確認 → 画面の描画。通信エラー・メンテナンス・ログイン切れは全体で拾う。
import './styles/base.css';
import './styles/layout.css';
import './styles/chat.css';
import './styles/screens/top.css';
import './styles/screens/stages.css';
import './styles/screens/worldmap.css';
import './styles/battle.css';
import './styles/screens/result.css';
import './styles/screens/enhance.css';
import './styles/screens/chara.css';
import './styles/screens/village.css';
import './styles/screens/gacha-shop.css';
import './styles/screens/omikuji.css';
import './styles/screens/haul.css';
import './styles/screens/lists.css';
import './styles/screens/login.css';
import './styles/screens/companion.css';
import './styles/screens/shiki-lv.css';
import './styles/screens/stories.css';

import './screens/login';
import './screens/top';
import './screens/stages';
import './screens/battle';
import './screens/result';
import './screens/chara';
import './screens/skills';
import './screens/enhance';
import './screens/village';
import './screens/gacha';
import './screens/shop';
import './screens/presents';
import './screens/survey';
import './screens/dex';
import './screens/social';
import './screens/settings';
import './screens/purchases';
import './screens/stories';
import './screens/help';
import './screens/support';
import './styles/screens/support.css';
import './styles/screens/contact.css';
import './styles/stamina-info.css';

import { api, ApiError, onApiError } from './api';
import { BOOT_SHEETS, loadSheets, portraitKey } from './assets';
import { bgm, SCENE_BGM, sfx, type SfxKey, unlockAudio } from './audio';
import { startAudioDebug } from './audio-debug';
import { currentName, go, onRoute, parse, render, setGuard } from './router';
import { onLoggedIn, onReplaced } from './session';
import { registerServiceWorker } from './ui/pwa';
import { applyMe, patch, refreshMe, state, subscribe } from './state';
import { initChat } from './ui/chat';
import { esc } from './ui/dom';
import { icon } from './ui/icons';
import { toast } from './ui/overlay';
import { initShell, setNewsLatest, setPointsChar, updateNav } from './ui/shell';
import { initSafeArea } from './ui/safe-area';
import { initViewport } from './ui/viewport';
import { initXPixel } from './ui/x-pixel';
import { captureSource } from './ui/source';
import { installTutorial } from './tutorial';
import { needsPrologue, prologueAtName } from './screens/prologue';
import './styles/screens/prologue.css';
import './styles/tutorial.css';
import './styles/mobile-comfort.css';
import './styles/consistency.css';

initViewport();
initSafeArea();
captureSource();
initXPixel();
installTutorial();

const app = document.getElementById('app')!;

setGuard((name) => {
  if (name === 'stories' || name === 'story') return null;
  if (state.maintenance) return 'top';
  // 初回の流れ（プロローグ）：名前より先に。プロローグの戦いは battle?prologue=1
  if (state.me && needsPrologue()) {
    if (name === 'prologue' || (name === 'battle' && parse().params.get('prologue') === '1')) return null;
    return 'prologue';
  }
  // 初回の流れで名前の入力まで来ていた：開き直してもログイン画面でなく、プロローグの名前入力から
  if (state.me && prologueAtName()) return name === 'prologue' ? null : 'prologue';
  if (!state.me || state.me.user.needsName) return 'login';
  if (name === 'login') return 'top';
  return null;
});
/**
 * 初回の流れ（プロローグ＝酒呑童子の戦い〜名前）の間はメニュー・タブを押せない（押すと読み直しになり物語が最初からになる）。
 * 現代の最初の戦い（狸）からは使える（負けても抜けられるように）
 */
function updateNavLock(name = currentName() ?? '') {
  app.classList.toggle('nav-locked', !state.me || needsPrologue() || !!state.me.user.needsName || name === 'prologue');
}
subscribe((ch) => {
  if (ch.has('progress') || ch.has('me') || ch.has('profile')) updateNavLock();
});
onRoute((name, s) => {
  updateNav(name, s);
  if (name !== 'chara' && name !== 'skills') setPointsChar(null);
  updateNavLock(name);
  app.classList.remove('chat-mobile-open');
  // 場面の曲（合戦・戦果は画面が自分で選ぶ）
  const music = SCENE_BGM[name];
  if (music) void bgm(music);
});

// ---------- 全体のエラー処理 ----------
let lastNetToast = 0;
onApiError((e: ApiError) => {
  if (e.status === 401 && state.me) {
    patch({ me: null });
    toast('ログインの有効期限が切れました。もう一度ログインしてください。', 'error', 4000);
    go('login', {}, true);
  } else if (e.status === 409 && e.code === 'replaced') {
    showReplaced();
  } else if (e.status === 503 && e.code === 'maintenance') {
    const body = e.body as { message?: string } | null;
    showMaintenance(body?.message || 'ただいまメンテナンス中です。');
  } else if (e.offline && Date.now() - lastNetToast > 5000) {
    lastNetToast = Date.now();
    patch({ online: navigator.onLine });
    toast('通信できませんでした。回線を確認してください。', 'error');
  }
});
window.addEventListener('online', () => patch({ online: true }));
window.addEventListener('offline', () => patch({ online: false }));
// iOS Safari は touchend・click でないと音の再開を許さないことがあるので、指を離したときにも試す
for (const ev of ['pointerdown', 'pointerup', 'touchend', 'click', 'keydown']) document.addEventListener(ev, unlockAudio, { capture: true, passive: true });
startAudioDebug();
// 押したときの音：指が触れた瞬間（pointerdown）に鳴らす（click は指を離した後なので遅れて聞こえる）。
// ボタン全般は軽いタップ、ナビ・タブ・閉じるは無音（耳障りなため）。
// 個別の音を鳴らす所は data-sfx="none"（タップを重ねない）か data-sfx="<キー>" で差し替える
document.addEventListener(
  'pointerdown',
  (e) => {
    if (!e.isPrimary || e.button > 0) return;
    const el = (e.target as HTMLElement).closest<HTMLElement>('button, a[href], [role="button"], [role="tab"], summary, select');
    if (!el || (el as HTMLButtonElement).disabled || el.getAttribute('aria-disabled') === 'true') return;
    const own = el.closest<HTMLElement>('[data-sfx]')?.dataset.sfx;
    if (own === 'none') return;
    if (own) return sfx(own as SfxKey);
    if (el.closest('[data-close]')) return; // 閉じるは無音
    if (el.matches('.sidenav a, .tabbar .tab, .tab-btn, [role="tab"], .drawer-list button')) return; // タブ・ナビの切り替えは無音（耳障りなため）
    sfx('tap', 0.6);
  },
  { capture: true },
);

function showMaintenance(message: string) {
  patch({ maintenance: { message } });
  let el = document.getElementById('maintenance');
  if (!el) {
    el = document.createElement('div');
    el.id = 'maintenance';
    el.className = 'fullscreen-state';
    document.body.appendChild(el);
  }
  el.innerHTML = `<div class="panel-paper deco fs-card" role="alert"><div class="fs-logo">百鬼国盗</div>${icon('gear')}<h1>メンテナンス中</h1><p class="pre">${esc(message)}</p>
    <button class="btn primary big" data-retry>再確認する</button></div>`;
  el.querySelector('[data-retry]')!.addEventListener('click', () => location.reload());
}

/** ほかの端末で開かれた：この画面は閉じる（オート周回も止める）。「この端末で続ける」で取り戻す */
function showReplaced() {
  if (document.getElementById('replaced')) return;
  onReplaced();
  void bgm(null);
  const el = document.createElement('div');
  el.id = 'replaced';
  el.className = 'fullscreen-state';
  el.innerHTML = `<div class="panel-paper deco fs-card" role="alert"><div class="fs-logo">百鬼国盗</div>${icon('info')}<h1>ほかの端末で開かれました</h1>
    <button class="btn primary big" data-retry>この端末で続ける</button></div>`;
  document.body.appendChild(el);
  el.querySelector('[data-retry]')!.addEventListener('click', async () => {
    await api.claim().catch(() => {});
    location.reload();
  });
}

function showBootError(text: string) {
  document.getElementById('screen')!.innerHTML = `<div class="fullscreen-state"><div class="panel-paper deco fs-card" role="alert"><div class="fs-logo">百鬼国盗</div>${icon('info')}<h1>読み込めませんでした</h1><p>${esc(text)}</p><p class="muted small">対応ブラウザ：最新の Chrome / Edge / Safari</p><button class="btn primary big" data-retry>再試行</button></div></div>`;
  document.querySelector('[data-retry]')!.addEventListener('click', () => location.reload());
}

async function boot() {
  (window as { __hkStarted?: boolean }).__hkStarted = true;
  // Stripe Checkout からの戻り（/shop/complete?session_id=… ・ /shop?canceled=1）をハッシュの画面へ
  if (location.pathname.startsWith('/shop')) {
    const q = new URLSearchParams(location.search);
    const sid = q.get('session_id');
    history.replaceState(null, '', `/${sid ? `#/shop?paid=${encodeURIComponent(sid)}` : q.get('canceled') ? '#/shop?canceled=1' : '#/shop'}`);
  }
  initShell();
  // 読み合わせリンクはログイン・ゲームAPI・全体チャットを待たず直接開く。
  if (['stories', 'story'].includes(parse().name)) {
    // 登録済みの名前だけ非同期で反映。未ログインでも読み合わせを続けられる。
    void api.me(true).then(me => {
      if (!state.me && ['stories', 'story'].includes(parse().name)) applyMe(me);
    }).catch(() => { /* 名前を取得できない場合は「あなた」で読む。 */ });
    (window as { __hkReady?: boolean }).__hkReady = true;
    await render();
    return;
  }
  initChat();
  // 初回ロード：起動に要る絵だけ（敵・建物はバトルに入るとき）。待ちすぎない（無くてもプレースホルダーで描ける）
  bootProgress(0.05, '素材を読み込んでいます…');
  await loadSheets(BOOT_SHEETS, (d, n) => bootProgress(0.05 + 0.6 * (d / Math.max(1, n)), `素材を読み込んでいます… ${d}/${n}`), 6000);
  bootProgress(0.7, 'サーバーに接続しています…');
  const st = await api.status().catch(() => null);
  if (st?.maintenance?.on) return showMaintenance(st.maintenance.message || 'ただいまメンテナンス中です。');
  if (st?.testMode) patch({ testMode: true });
  if (st?.announcements?.length) setNewsLatest(Math.max(...st.announcements.map((a) => a.startsAt)));
  try {
    await api.claim().catch(() => {}); // 開いた端末をいま遊んでいる端末にする
    await refreshMe();
    bootProgress(0.85, '');
    await loadSheets([portraitKey(state.oshi)], undefined, 4000);
    if (!state.me?.user.needsName) await onLoggedIn({ skipBonus: needsPrologue() });
  } catch (e) {
    if (!import.meta.env.DEV && e instanceof ApiError && e.status !== 401 && e.status !== 0 && !e.notReady) return showBootError(`サーバーでエラーが発生しました（${e.status}）。`);
    // 未ログイン・オフライン → ログイン画面（開発時は見本データのセッションを復元）
    if (import.meta.env.DEV && sessionStorage.getItem('hk.mock')) {
      const { mockMe } = await import('./dev');
      applyMe(mockMe(sessionStorage.getItem('hk.mock')!));
    }
  }
  if (!location.hash) history.replaceState(null, '', '#/top');
  bootProgress(1, '');
  (window as { __hkReady?: boolean }).__hkReady = true;
  await render();
}

/** 起動画面の進捗（0..1） */
function bootProgress(v: number, text: string) {
  const bar = document.getElementById('boot-bar');
  if (bar) bar.style.width = `${Math.round(v * 100)}%`;
  const t = document.getElementById('boot-text');
  if (t && text) t.textContent = text;
}

registerServiceWorker();
boot().catch((e) => showBootError(String(e)));
